'use client';

import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2, Briefcase, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loading } from '@/app/template/frontend/global/Loading';
import { signup, checkUserExists } from '@/app/signup/actions';
import { trackUserAction } from '@/app/actions/posthog';
import { PostHogEventName } from '@/types/posthog';
import { useOnboardingStore, type UserType } from '../../store';
import { updateCompanyOnboarding, acceptTerms } from '../../actions';
import { StepWrapper } from '../shared/StepWrapper';
import { Checkbox } from '@/components/ui/checkbox';

const accountSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, 'Email is required').email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  termsAccepted: z.literal(true, { errorMap: () => ({ message: 'You must accept the terms to continue' }) }),
});

type AccountFormData = z.infer<typeof accountSchema>;

interface AccountStepProps {
  onNext: () => void;
  isInvitedUser: boolean;
}

const USER_TYPES: { value: UserType; label: string; description: string; icon: React.ReactNode }[] = [
  {
    value: 'brand',
    label: "I'm on a brand's team",
    description: 'I work directly for an eCommerce brand',
    icon: <Building2 className="h-5 w-5" />,
  },
  {
    value: 'agency',
    label: 'I work for an agency / as a freelancer',
    description: "I'll manage client workspaces",
    icon: <Briefcase className="h-5 w-5" />,
  },
];

export function AccountStep({ onNext, isInvitedUser }: AccountStepProps) {
  const store = useOnboardingStore();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [domainWarning, setDomainWarning] = useState<string | null>(null);
  const bypassDomainCheckRef = useRef(false);
  const [formSubmitted, setFormSubmitted] = useState(store.isAuthenticated);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    setValue,
  } = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    mode: 'onBlur',
    defaultValues: {
      firstName: store.firstName,
      lastName: store.lastName,
      email: store.email,
      termsAccepted: undefined as unknown as true,
    },
  });

  // I1 fix: Use useEffect for invited user auto-advance instead of render-time call
  useEffect(() => {
    if (formSubmitted && isInvitedUser) {
      onNext();
    }
  }, [formSubmitted, isInvitedUser, onNext]);

  const onSubmitForm = async (data: AccountFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Check if user already exists
      const userCheck = await checkUserExists(data.email);
      if (userCheck.exists) {
        setSubmitError('Account with this email already exists. Please sign in instead.');
        setIsSubmitting(false);
        return;
      }

      // Check domain conflicts (unless user chose to bypass)
      if (!bypassDomainCheckRef.current) {
        const domainCheckResponse = await fetch('/api/signup/check-domain', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: data.email }),
        });

        if (domainCheckResponse.ok) {
          const domainResult = await domainCheckResponse.json();
          if (domainResult.exists) {
            setDomainWarning(domainResult.message);
            setIsSubmitting(false);
            return;
          }
        }
      }

      // Create FormData for the existing signup action
      const formData = new FormData();
      formData.append('email', data.email);
      formData.append('password', data.password);
      formData.append('name', `${data.firstName} ${data.lastName}`);
      formData.append('website', ''); // Honeypot - always empty

      const result = await signup(formData);

      if (!result.success) {
        setSubmitError(result.error || 'Signup failed');
        setIsSubmitting(false);
        return;
      }

      // Store auth data
      store.setAccountData({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
      });

      if (result.userId) {
        store.setAuthData({
          userId: result.userId,
          companyId: result.companyId || '',
        });

        // Record terms acceptance
        if (result.companyId) {
          await acceptTerms(result.companyId, result.userId);
        }

        // Track signup in PostHog
        trackUserAction(
          data.email,
          result.companyId || '',
          PostHogEventName.ONBOARDING_WIZARD_STARTED,
          { user_type: 'new', email: data.email }
        );
      }

      setFormSubmitted(true);
    } catch {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUserTypeSelect = async (type: UserType) => {
    store.setUserType(type);

    // I2 fix: Update user type via server action (which now also sets is_agency)
    if (store.companyId) {
      updateCompanyOnboarding(store.companyId, { userType: type });

      // C1: Track user type selection
      trackUserAction(
        store.email,
        store.companyId,
        PostHogEventName.ONBOARDING_WIZARD_USER_TYPE_SELECTED,
        { user_type: type }
      );
    }

    onNext();
  };

  // If already authenticated (page refresh), show only user type selection
  if (formSubmitted && !isInvitedUser) {
    return (
      <StepWrapper
        title="Tell us who you are"
      >
        <div className="space-y-3">
          {USER_TYPES.map((type, index) => (
            <motion.button
              key={type.value}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => handleUserTypeSelect(type.value)}
              className="w-full flex items-center gap-4 p-4 rounded-[13px] border bg-white text-left transition-colors duration-200 hover:bg-primary/5 hover:border-primary/20 group"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-kinetic-light-pink flex items-center justify-center text-foreground/60">
                {type.icon}
              </div>
              <div>
                <div className="font-medium">{type.label}</div>
                <div className="text-sm text-foreground/50">{type.description}</div>
              </div>
            </motion.button>
          ))}
        </div>
      </StepWrapper>
    );
  }

  // For invited users, useEffect handles auto-advance — show loading
  if (formSubmitted && isInvitedUser) {
    return (
      <StepWrapper title="Setting up your workspace...">
        <div className="flex justify-center py-8">
          <Loading />
        </div>
      </StepWrapper>
    );
  }

  return (
    <StepWrapper
      title="Welcome to Kinetic!"
      subtitle="Let's get you set up in under a minute."
    >
      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
        {submitError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        {domainWarning && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
            <p
              className="text-sm text-amber-800 [&_a]:underline [&_a]:font-medium [&_a]:text-amber-900"
              dangerouslySetInnerHTML={{ __html: domainWarning }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full border-amber-300 text-amber-800 hover:bg-amber-100"
              onClick={() => {
                bypassDomainCheckRef.current = true;
                setDomainWarning(null);
                handleSubmit(onSubmitForm)();
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loading /> : 'Register anyway'}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="firstName">First name</Label>
            <Input
              id="firstName"
              placeholder="Jane"
              {...register('firstName')}
            />
            {errors.firstName && (
              <p className="text-xs text-red-500">{errors.firstName.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lastName">Last name</Label>
            <Input
              id="lastName"
              placeholder="Doe"
              {...register('lastName')}
            />
            {errors.lastName && (
              <p className="text-xs text-red-500">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="jane@company.com"
            {...register('email')}
          />
          {errors.email && (
            <p className="text-xs text-red-500">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              {...register('password')}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-red-500">{errors.password.message}</p>
          )}
        </div>

        {/* Honeypot */}
        <div aria-hidden="true" className="absolute -left-[9999px] -top-[9999px] opacity-0 h-0 overflow-hidden pointer-events-none">
          <Input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
        </div>

        <div className="flex items-start gap-2">
          <Checkbox
            id="termsAccepted"
            className="mt-0.5 bg-white border-[#fef0ff]"
            onCheckedChange={(checked) => setValue('termsAccepted', checked === true ? true : undefined as unknown as true)}
          />
          <label htmlFor="termsAccepted" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
            I agree to the{' '}
            <a href="https://www.usekinetic.com/terms" target="_blank" className="underline underline-offset-4 hover:text-foreground">Terms of Service</a>{' '}
            and{' '}
            <a href="https://www.usekinetic.com/privacy" target="_blank" className="underline underline-offset-4 hover:text-foreground">Privacy Policy</a>
          </label>
        </div>
        {errors.termsAccepted && (
          <p className="text-xs text-red-500">{errors.termsAccepted.message}</p>
        )}

        <Button
          type="submit"
          className="w-full bg-primary hover:bg-primary/90 rounded-[13px] h-12 text-base"
          disabled={isSubmitting}
        >
          {isSubmitting ? <Loading /> : 'Continue'}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          Already have an account?{' '}
          <a href="/login" className="underline underline-offset-4 hover:text-foreground">
            Log in
          </a>
        </p>
      </form>
    </StepWrapper>
  );
}
